import React, { useState, useRef, useCallback, useContext, useEffect, useLayoutEffect } from 'react';
import { bkkAt, bkkNow, fmtBkk, toBkkInput, fromBkkInput } from '../../utils/bkkTime';
import { useTranslation } from 'react-i18next';
import { useStore, replyModeOf } from '../../store';
import { format } from 'date-fns';
import { api, apiFetch } from '../../utils/api';
import { labelView } from '../../utils/emailLabels';
import Composer from '../Composer';
import { Email } from '../../types';
import AddToLeadModal from '../Leads/AddToLeadModal';
import { ContactForm } from '../Contact';
import RectifyModal, { canRectify, doorsOf } from './RectifyModal';

function formatBytes(bytes: number): string {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' KB';
  return (bytes / 1024 / 1024).toFixed(1) + ' MB';
}

// Also used by the email list's attachment chips (Default density), so both show the same icon.
export function fileIcon(contentType: string): string {
  if (contentType.startsWith('image/')) return 'ti-photo';
  if (contentType.includes('pdf')) return 'ti-file-type-pdf';
  if (contentType.includes('word') || contentType.includes('document')) return 'ti-file-type-doc';
  if (contentType.includes('spreadsheet') || contentType.includes('excel')) return 'ti-file-type-xls';
  if (contentType.includes('zip') || contentType.includes('rar') || contentType.includes('7z')) return 'ti-file-zip';
  return 'ti-file';
}

// The file's bytes, fetched with the login token (a plain link can't carry it) — null if the server
// refuses. Shared by Download and the email list's attachment preview.
async function fetchAttachment(emailId: string, attId: string): Promise<Blob | null> {
  const token = localStorage.getItem('mf_token');
  const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
  const res = await apiFetch(`${apiBase}/emails/${emailId}/attachments/${attId}`, {   // a restart's failure brings up the notice
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.ok ? res.blob() : null;
}

function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
}

export async function downloadAttachment(emailId: string, attId: string, filename: string) {
  const blob = await fetchAttachment(emailId, attId);
  if (blob) saveBlob(blob, filename);
}

// What the attachment preview can draw in the page: PDFs (the browser's own PDF viewer), pictures and
// plain text. Judged by the file's type AND its name — some mailers send a PDF as a generic
// "application/octet-stream". Anything else (Word, Excel, zip…) gets the no-preview card.
type PreviewKind = 'pdf' | 'image' | 'text' | 'none';
function previewKind(type: string, name: string): PreviewKind {
  const ct = (type || '').toLowerCase().split(';')[0].trim();
  const ext = (/\.([a-z0-9]+)$/i.exec(name)?.[1] || '').toLowerCase();
  if (ct.includes('pdf') || ext === 'pdf') return 'pdf';
  if (/^image\/(png|jpe?g|gif|webp|bmp|svg\+xml|avif)$/.test(ct) || ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg', 'avif'].includes(ext)) return 'image';
  if (ct === 'text/plain' || ct === 'text/csv' || ['txt', 'csv', 'log'].includes(ext)) return 'text';
  return 'none';
}

// Translate in the preview (Calem, Bugify #87afb73e): the languages offered, and which drawn files
// Claude can read — every PDF and text file, but of the pictures only JPG / PNG / GIF / WEBP (the
// preview also draws BMP, SVG and AVIF, which get no Translate).
type DocLang = 'th' | 'en' | 'my';
const DOC_LANGS: DocLang[] = ['th', 'en', 'my'];
function canTranslate(kind: PreviewKind, type: string, name: string): boolean {
  if (kind === 'pdf' || kind === 'text') return true;
  if (kind !== 'image') return false;
  const ct = (type || '').toLowerCase().split(';')[0].trim();
  const ext = (/\.([a-z0-9]+)$/i.exec(name)?.[1] || '').toLowerCase();
  return /^image\/(png|jpe?g|gif|webp)$/.test(ct) || ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext);
}

// Attachment preview — opened from an attachment chip in the email list, over the whole page, instead
// of downloading the file (Calem, Bugify #e0e30d4b screenshot-2). File name + size, Download, close ×
// (Esc, or a click on the dark backdrop, also close it). HTML files are never drawn here: the file is
// served from the app's own origin, so only PDF (typed as PDF), <img> and escaped text are used.
// Beside Download: a language and Translate, which shows the translation in place of the file, and
// Original to go back. The server escapes the translation before wrapping it in <p>, so it is drawn
// as HTML here.
// `onRectify` (report 990a6f19): an open email's own preview also offers Rectify (AI) on a JPEG, PNG or
// PDF — the attachments panel below closes the preview and opens RectifyModal. The email list's preview
// passes none.
export function AttachmentPreview({ emailId, att, onClose, onRectify }: { emailId: string; att: { id: string; name: string; size: number; type: string }; onClose: () => void; onRectify?: () => void }) {
  const { t } = useTranslation();
  const uiLang = useStore(s => s.uiLang);
  const [kind, setKind] = useState<PreviewKind>(() => previewKind(att.type, att.name));
  const [blob, setBlob] = useState<Blob | null>(null);
  const [url, setUrl] = useState('');
  const [text, setText] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  // The language picked, the translation on screen ('' = the file itself), and each language's
  // result and request in flight — kept for as long as the preview is open.
  const [lang, setLang] = useState<DocLang>(uiLang === 'th' ? 'th' : 'en');
  const [onScreen, setOnScreen] = useState<DocLang | ''>('');
  const [done, setDone] = useState<Record<string, { html: string; truncated: boolean }>>({});
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const [trError, setTrError] = useState('');
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  useEffect(() => {
    const k = previewKind(att.type, att.name);
    if (k === 'none') return;   // nothing to draw — the file is fetched only if they press Download
    let live = true, made = '';
    fetchAttachment(emailId, att.id).then(async b => {
      if (!live) return;
      if (!b) { setFailed(true); return; }
      setBlob(b);
      if (k === 'text') { const s = await b.text(); if (live) setText(s); return; }
      // The browser's PDF viewer only opens a file marked as PDF; anything else it downloads again.
      made = URL.createObjectURL(k === 'pdf' ? new Blob([b], { type: 'application/pdf' }) : b);
      setUrl(made);
    }).catch(() => { if (live) setFailed(true); });
    return () => { live = false; if (made) URL.revokeObjectURL(made); };
  }, [emailId, att.id, att.type, att.name]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // The bytes already fetched for the preview are saved as they are; otherwise they are fetched now.
  const download = () => { if (blob) saveBlob(blob, att.name); else void downloadAttachment(emailId, att.id, att.name); };
  const shown = kind !== 'none' && !failed;
  const translatable = shown && canTranslate(kind, att.type, att.name);
  const langName = (l: DocLang) => t(`eml_att_lang_${l}`);

  // Show language l's translation: at once if it is already here, otherwise a spinner until it comes.
  // A failure puts the file back on screen with the reason in a line under the header.
  const translate = (l: DocLang) => {
    setTrError('');
    setOnScreen(l);
    if (done[l] || busy[l]) return;
    setBusy(b => ({ ...b, [l]: true }));
    api.translateAttachment(emailId, att.id, l)
      .then(r => { if (alive.current) setDone(d => ({ ...d, [l]: { html: r.translated || '', truncated: !!r.truncated } })); })
      .catch((e: any) => {
        if (!alive.current) return;
        setOnScreen(s => (s === l ? '' : s));
        // A proxy's own error page (nginx's 504) is markup, not a reason — req() flags it htmlPage.
        const why = e?.htmlPage ? '' : String(e?.message || '');
        setTrError(t('eml_att_translate_failed', { lang: langName(l) }) + (why ? ` — ${why}` : ''));
      })
      .finally(() => { if (alive.current) setBusy(b => ({ ...b, [l]: false })); });
  };
  // While a translation is on screen, picking another language shows that one; otherwise the pick
  // waits for Translate.
  const pickLang = (l: DocLang) => { setLang(l); if (onScreen) translate(l); };

  const spinner = (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <i className="ti ti-loader-2" style={{ fontSize: 26, color: 'var(--text-tertiary)', animation: 'spin 1s linear infinite' }} aria-hidden />
    </div>
  );
  const downloadBtn = (
    <button onClick={download} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 14px', fontSize: 12, fontWeight: 600, borderRadius: 'var(--radius-md)', border: 'none', background: 'var(--accent)', color: '#fff', cursor: 'pointer', flexShrink: 0 }}>
      <i className="ti ti-download" style={{ fontSize: 13 }} aria-hidden />{t('eml_att_download_btn')}
    </button>
  );
  // Outlined in the accent beside the filled Download; Translate is washed while its language is on screen.
  const outlineBtn = (on: boolean): React.CSSProperties => ({ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', fontSize: 12, fontWeight: 600, borderRadius: 'var(--radius-md)', border: '1px solid var(--accent)', background: on ? 'var(--accent-bg)' : 'transparent', color: 'var(--accent)', cursor: 'pointer', flexShrink: 0 });
  const translateControls = (
    <>
      <select value={lang} onChange={e => pickLang(e.target.value as DocLang)} title={t('eml_att_translate_to')}
        style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', background: 'var(--c-gray-50)', fontSize: 12, color: 'var(--text-primary)', padding: '6px 8px', cursor: 'pointer', outline: 'none', fontWeight: 500, flexShrink: 0 }}>
        {DOC_LANGS.map(l => <option key={l} value={l}>{langName(l)}</option>)}
      </select>
      <button onClick={() => translate(lang)} style={outlineBtn(onScreen === lang)}>
        <i className={`ti ${busy[lang] ? 'ti-loader-2' : 'ti-language'}`} style={{ fontSize: 13, animation: busy[lang] ? 'spin 1s linear infinite' : undefined }} aria-hidden />{t('eml_att_translate_btn')}
      </button>
      {onScreen && (
        <button onClick={() => setOnScreen('')} style={outlineBtn(false)}>
          <i className="ti ti-file-text" style={{ fontSize: 13 }} aria-hidden />{t('eml_att_original_btn')}
        </button>
      )}
    </>
  );
  const shownTr = onScreen ? done[onScreen] : undefined;
  const translation = shownTr ? (
    <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: 16, background: 'var(--bg-panel)' }}>
      <div lang={onScreen} className="rte-render rte-lg" dangerouslySetInnerHTML={{ __html: shownTr.html }}
        style={{ maxWidth: 820, margin: '0 auto', padding: '26px 32px', background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)' }} />
      {shownTr.truncated && (
        <div style={{ maxWidth: 820, margin: '10px auto 0', fontSize: 12, color: 'var(--text-tertiary)' }}>{t('eml_att_translate_cut')}</div>
      )}
    </div>
  ) : (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
      <i className="ti ti-loader-2" style={{ fontSize: 26, color: 'var(--text-tertiary)', animation: 'spin 1s linear infinite' }} aria-hidden />
      {onScreen && <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>{t('eml_att_translating', { lang: langName(onScreen) })}</div>}
    </div>
  );

  return (
    <div data-att-preview onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 5000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div onClick={e => e.stopPropagation()} style={{ width: shown ? 'min(1100px, 100%)' : 'min(420px, 100%)', height: shown ? '100%' : undefined, maxHeight: '100%', display: 'flex', flexDirection: 'column', background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', boxShadow: 'var(--shadow-md)', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px 12px 18px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
          <i className={`ti ${fileIcon(att.type || '')}`} style={{ fontSize: 20, color: 'var(--accent)', flexShrink: 0 }} aria-hidden />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div title={att.name} style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{att.name}</div>
            <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{formatBytes(att.size)}</div>
          </div>
          {translatable && translateControls}
          {onRectify && shown && canRectify(att.type, att.name) && (
            <button onClick={onRectify} style={outlineBtn(false)}>
              <i className="ti ti-wand" style={{ fontSize: 13 }} aria-hidden />{t('eml_rect_btn')}
            </button>
          )}
          {shown && downloadBtn}
          <button onClick={onClose} title={t('eml_att_close')} style={{ width: 30, height: 30, borderRadius: '50%', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <i className="ti ti-x" style={{ fontSize: 15 }} aria-hidden />
          </button>
        </div>
        {translatable && trError && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 18px', fontSize: 12, color: '#E24B4A', background: 'color-mix(in srgb, #E24B4A 7%, transparent)', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
            <i className="ti ti-alert-circle" style={{ fontSize: 14, flexShrink: 0 }} aria-hidden />{trError}
          </div>
        )}
        {!shown ? (
          /* Word / Excel / zip…, a picture this browser can't draw, or a file that failed to load */
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, padding: '30px 24px 28px', textAlign: 'center' }}>
            <i className={`ti ${failed ? 'ti-file-alert' : fileIcon(att.type || '')}`} style={{ fontSize: 44, color: failed ? '#E24B4A' : 'var(--text-tertiary)' }} aria-hidden />
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-secondary)' }}>{t(failed ? 'eml_att_load_failed' : 'eml_att_no_preview')}</div>
            {downloadBtn}
          </div>
        ) : translatable && onScreen ? (
          translation
        ) : kind === 'pdf' ? (
          url ? <iframe src={url} title={att.name} style={{ flex: 1, minHeight: 0, width: '100%', border: 'none', background: 'var(--bg-panel)' }} /> : spinner
        ) : kind === 'image' ? (
          url ? (
            <div style={{ flex: 1, minHeight: 0, display: 'flex', overflow: 'auto', padding: 16, background: 'var(--bg-panel)' }}>
              <img src={url} alt={att.name} onError={() => setKind('none')} style={{ maxWidth: '100%', maxHeight: '100%', margin: 'auto', objectFit: 'contain', borderRadius: 4 }} />
            </div>
          ) : spinner
        ) : text !== null ? (
          <pre style={{ flex: 1, minHeight: 0, overflow: 'auto', margin: 0, padding: '14px 18px', fontFamily: 'var(--font-mono)', fontSize: 12.5, lineHeight: 1.55, whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: 'var(--text-primary)' }}>{text}</pre>
        ) : spinner}
      </div>
    </div>
  );
}

// The "opened" image a quotation's To: copy carries (Calem, Bugify #bf7d6a54). Our Sent copy has it
// too, so it is taken out before anything is drawn — reading your own email must never count as the
// client opening it. Same pattern as the server's stripOpenPixel (services/peakPublic.ts).
const OPEN_PIXEL = /<img\b[^>]*\/api\/public\/peak\/[^"'\s>/]+\/open\.gif[^>]*>/gi;

// Rectify (AI) on a picture in the body (report 990a6f19, opening #3: "Allow screenshots in the body of the email to
// be rectified as well as attachments"). A screenshot pasted into a mail arrives as an inline part, which sync writes
// into the body as a data: URI and keeps no attachment row of (server/src/services/imap.ts), so the attachments
// panel's wand never reaches it. Given `onRectifyImage`, the frame lays the same wand over each JPEG or PNG the body
// draws from a data: URI, 6px in from the picture's top-right corner — once the picture is big enough to be a
// screenshot or a scan (200px a side in its own pixels), so a signature's logo or an icon gets none. `n` counts every
// such picture in the body, small ones too, from 0 in source order: the number the server finds the same picture by
// (routes/emails.ts bodyPicture, whose DATA_PIC is BODY_PIC — keep the two alike). A picture the body links to on the
// web gets no wand: the server never fetches an address. The wands sit over the frame, not in it — the frame runs no
// script, and its own CSS never reaches them.
const BODY_PIC = /^\s*data:image\/(?:jpe?g|png)\b[^,]*;base64,/i;
const PIC_MIN = 200;
const WAND = 26;
type Wand = { n: number; left: number; top: number };
function wandsIn(doc: Document): Wand[] {
  const wands: Wand[] = [];
  const frameW = doc.documentElement.clientWidth;   // less a scroll bar
  let n = -1;
  for (const img of Array.from(doc.images)) {
    if (!BODY_PIC.test(img.getAttribute('src') || '')) continue;
    n++;
    if (img.naturalWidth < PIC_MIN || img.naturalHeight < PIC_MIN) continue;
    const r = img.getBoundingClientRect();
    // Hidden, or drawn too small to carry a wand without covering it — a sharp logo drawn at half its size.
    if (r.width < 2 * WAND || r.height < 2 * WAND) continue;
    // A picture wider than the frame keeps its wand in sight, at the frame's right edge.
    wands.push({ n, left: Math.max(r.left, Math.min(r.right, frameW) - WAND - 6), top: r.top + 6 });
  }
  return wands;
}

export function HtmlFrame({ html: raw, onRectifyImage }: { html: string; onRectifyImage?: (n: number) => void }) {
  const { t } = useTranslation();
  const html = (raw || '').replace(OPEN_PIXEL, '');
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(200);
  const [wands, setWands] = useState<Wand[]>([]);
  const withWands = !!onRectifyImage;
  // Where the wands go, read from the frame each time it is measured and whenever it changes size.
  const placeWands = useCallback(() => {
    if (!withWands) return;
    try {
      const doc = iframeRef.current?.contentDocument;
      if (doc) setWands(wandsIn(doc));
    } catch {}
  }, [withWands]);
  const onLoad = useCallback(() => {
    const measure = () => {
      const iframe = iframeRef.current;
      if (!iframe) return;
      try {
        const doc = iframe.contentDocument || iframe.contentWindow?.document;
        if (doc) setHeight(Math.max(80, (doc.documentElement.scrollHeight || doc.body.scrollHeight) + 20));
      } catch {}
      placeWands();
    };
    measure();
    // Images/webfonts finish after the load event — re-measure so tall bodies aren't cut off.
    setTimeout(measure, 300);
    setTimeout(measure, 1200);
  }, [placeWands]);
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!withWands || !iframe) return;
    const ro = new ResizeObserver(placeWands);
    ro.observe(iframe);
    return () => ro.disconnect();
  }, [withWands, placeWands]);
  // Inject <base target="_blank"> so all links open in new tab, not navigate the app
  const safeHtml = html.includes('<head>')
    ? html.replace('<head>', '<head><base target="_blank" rel="noopener noreferrer">')
    : `<base target="_blank" rel="noopener noreferrer">${html}`;
  // Another body: the last one's wands go before it loads.
  useLayoutEffect(() => { setWands(w => (w.length ? [] : w)); }, [safeHtml]);
  const frame = (
    <iframe ref={iframeRef} srcDoc={safeHtml} onLoad={onLoad} title="email-body"
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      style={{ border: 'none', width: '100%', height, display: 'block' }} />
  );
  if (!withWands) return frame;
  return (
    <div style={{ position: 'relative' }}>
      {frame}
      {wands.map(w => (
        <button key={w.n} onClick={() => onRectifyImage(w.n)} title={t('eml_rect_btn')} aria-label={t('eml_rect_btn')}
          style={{ position: 'absolute', left: w.left, top: w.top, width: WAND, height: WAND, padding: 0, borderRadius: '50%', border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', color: 'var(--accent)', boxShadow: 'var(--shadow-md)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <i className="ti ti-wand" style={{ fontSize: 14 }} aria-hidden />
        </button>
      ))}
    </div>
  );
}

function ConfirmDialog({ message, onConfirm, onCancel }: { message: string; onConfirm: () => void; onCancel: () => void }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', boxShadow: 'var(--shadow-md)', padding: '24px 28px', width: 340, textAlign: 'center' }}>
        <i className="ti ti-alert-triangle" style={{ fontSize: 32, color: '#E24B4A', marginBottom: 12, display: 'block' }} aria-hidden />
        <p style={{ fontSize: 14, color: 'var(--text-primary)', marginBottom: 20, lineHeight: 1.5 }}>{message}</p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
          <button onClick={onCancel} style={{ padding: '8px 20px', fontSize: 13, borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer' }}>Cancel</button>
          <button onClick={onConfirm} style={{ padding: '8px 20px', fontSize: 13, borderRadius: 'var(--radius-md)', border: 'none', background: '#E24B4A', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>Confirm</button>
        </div>
      </div>
    </div>
  );
}

// Blank room below the end of an open email for the module's floating "+" (52px, 24px in from the
// bottom-right corner), so the last attachments and buttons scroll up clear of it, not beneath it.
const FAB_ROOM = 88;

// Attachments panel — shared between both tabs and every message of a conversation. Clicking a chip
// previews its file over the page, as the email list's chips do (Calem, Bugify #fcd1ede7: "whilst
// you're inside the email, allow click preview like we already have in email preview mode"); its ⤓
// still just downloads. 20px of room below it, where it sat on the bottom edge ("pressed against the
// bottom bar"): a margin, which no scroll area squeezes away.
// A JPEG, PNG or PDF chip also carries a wand before its ⤓: Rectify (AI) (report 990a6f19, RectifyModal).
function AttachmentsPanel({ emailId, attachments }: { emailId: string; attachments: any[] }) {
  const { t } = useTranslation();
  const [preview, setPreview] = useState<any>(null);
  const [rectify, setRectify] = useState<any>(null);
  if (attachments.length === 0) return null;
  const chipBtn: React.CSSProperties = { border: 'none', background: 'none', color: 'var(--accent)', cursor: 'pointer', padding: 4, borderRadius: 'var(--radius-sm)', display: 'flex', alignItems: 'center', flexShrink: 0 };
  return (
    <div style={{ margin: '16px 20px 20px', paddingTop: 12, borderTop: '1px solid var(--border)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 }}>
        <i className="ti ti-paperclip" style={{ fontSize: 13 }} aria-hidden />
        {attachments.length} attachment{attachments.length > 1 ? 's' : ''}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {attachments.map((att: any) => (
          <div key={att.id} onClick={() => setPreview(att)} title={t('eml_att_preview', { name: att.name })} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: 'var(--c-gray-50)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', maxWidth: 240, cursor: 'pointer' }}>
            <i className={`ti ${fileIcon(att.type || '')}`} style={{ fontSize: 18, color: 'var(--accent)', flexShrink: 0 }} aria-hidden />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{att.name}</div>
              <div style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>{formatBytes(att.size)}</div>
            </div>
            {canRectify(att.type || '', att.name || '') && (
              <button onClick={e => { e.stopPropagation(); setRectify(att); }} title={t('eml_rect_btn')} style={chipBtn}>
                <i className="ti ti-wand" style={{ fontSize: 14 }} aria-hidden />
              </button>
            )}
            <button onClick={e => { e.stopPropagation(); downloadAttachment(emailId, att.id, att.name); }} title="Download" style={chipBtn}>
              <i className="ti ti-download" style={{ fontSize: 14 }} aria-hidden />
            </button>
          </div>
        ))}
      </div>
      {preview && <AttachmentPreview key={emailId + preview.id} emailId={emailId} att={preview} onClose={() => setPreview(null)}
        onRectify={() => { setRectify(preview); setPreview(null); }} />}
      {rectify && <RectifyModal key={emailId + rectify.id} doors={doorsOf(emailId, { att: rectify })} fileName={rectify.name || ''} onClose={() => setRectify(null)} />}
    </div>
  );
}

// System folder names — the same lookup the email list header uses for its title.
const FOLDER_NAMES: Record<string, string> = {
  inbox: 'Inbox', sent: 'Sent', starred: 'Starred',
  archive: 'Archive', drafts: 'Drafts', trash: 'Trash',
};

// ── The reading pane's action bar (Calem, Bugify #e5a0a527) ──────────────────────────────────────
// Both reading panes — a single email's and a conversation's — carry their actions in ONE short row
// of small icons in the pane's header: in a single email in the free space beside the Original /
// Translation tabs, starting 20px after them and standing 10px above the header's bottom line ("Make
// these icons 10% larger and bring them up 10px from the gray line then, left align (20px) away from
// the original/translation tabs", Bugify #3146d7e0); in a conversation at the right end of the
// header's top line, past the reply target.
// The first opening of this report made it a column of 44px squares down the pane's left edge, on
// the seam with the email list; the third puts it back where it was, smaller and in line with the
// tabs ("Reduce the size of the icons and put them back to where they were before, except align
// them to the two tabs this time", with Calem's mock).
// The row says its buttons' NAMES whenever it has room for all of them side by side, and is the
// short line of icons when it has not ("Keep these as icons if there is not enough room on the screen
// for them to fit on one row but if there is enough room, change them to their names", Bugify
// #194ccc7a): with the names up every button is at rest the very pill it grows into under the pointer,
// and the pointer changes nothing; with them down it is the small square again, carrying its label as
// its tooltip (title + aria-label) so nothing is lost, and saying its name in the pill it grows into
// while the pointer (or the keyboard) is on it — one name at a time (Bugify #338c1651, BarButton).
// Which of the two it is comes from the row's own measurement (useCompactBar below), as it did for
// Bugify #68f6a9c9; in between the two reports the row was icon-only whatever the room.
// Left to right the row keeps the order the column ran top to bottom — Back · Unread · Delete ·
// Reply / Reply All / Forward · Spam · Archive · Snooze · Label — each a small square in its own
// group's tint and outline, 11px from the next: BAR_BTN 30.4095px around a BAR_ICON 16.2184px
// icon. The gap was 6px; the first opening of Bugify #5d81745e made it 8px ("Add 2 pixels of space
// (horizontally) between each icon"), the second 11px ("Add another 3 pixels of space",
// Bugify #5d81745e, opening #2).
// The first opening of Bugify #3146d7e0 made #e5a0a527's 30px around 16px 10% larger, 33px around
// 17.6px; the second took 5% back off that, 31.35px around 16.72px ("Make the icons 5% smaller",
// Bugify #3146d7e0, opening #2); the third takes another 3% off those ("Make these icons 3%
// smaller", Bugify #3146d7e0, opening #3). Each step is kept exact, never rounded. The column's were
// 44px around 24px. Both panes share the two sizes.
const BAR_ICON = 16.2184;
const BAR_BTN = 30.4095;
// The row sits at the right end of the header row it is given, its buttons standing on that row's
// bottom line — as it does in a conversation. A single email overrides both: its row starts at the
// left, right after the tabs, and is lifted off the header's bottom line (see the tab row). A caller
// inside a flex ROW also passes `flex: 1` to claim the free space; never pass it inside a flex
// COLUMN — there it is the height that would grow.
// While the names are up the row is nowrap (the caller sets it): it would rather drop the names than
// break them onto a second line, which is the whole of Bugify #68f6a9c9. Wrapping is left to the
// icon row as its last resort — a pane too narrow for even the icons stacks them instead of letting
// them run over the tabs beside them, and that needs a pane narrower than the list ever leaves.
const barStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end', gap: 11, flexWrap: 'wrap',
};

// ── Names up or icons alone: the row measures itself (Bugify #68f6a9c9, #194ccc7a) ───────────────
// None of the row's buttons shrink (flexShrink 0) and with the names up the row never wraps, so the
// buttons always stand at their full width and the width they need is the distance from the first
// one's left edge to the last one's right — true even when they already spill over, so it is known
// before anyone has to see the spill. It is measured after every render (the row gains and loses
// buttons — Snooze, Label, Reply all, a reply in progress) and whenever the row is resized (the
// split-screen divider), and remembered; the names come down as soon as the room is narrower than
// that. It is never measured while they are down — a row of icons always fits, so the row would flip
// back and forth — which is why the names' width is kept until the names themselves are back up.
// A child that fills the free space beside the buttons (a conversation's reply target) is marked
// data-grow and left out of the measurement: it gives way instead of taking room.
// The measured element is the one whose width is the ROOM the buttons are given, never their own
// content: in a single email that is the bar itself (it claims the free space beside the tabs with
// flex 1), in a conversation the header line the bar ends, where the bar keeps its content's width.
function barContentWidth(el: HTMLElement): number {
  const kids = (Array.from(el.children) as HTMLElement[]).filter(k => !k.hasAttribute('data-grow'));
  if (kids.length === 0) return 0;
  return kids[kids.length - 1].getBoundingClientRect().right - kids[0].getBoundingClientRect().left;
}
function useCompactBar(): [React.RefObject<HTMLDivElement>, boolean] {
  const ref = useRef<HTMLDivElement>(null);
  const [compact, setCompact] = useState(false);
  const need = useRef(0);
  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const room = el.clientWidth;
    if (!room) return;                                  // not on screen (no pane, or a hidden tab)
    if (!compact) need.current = barContentWidth(el);    // names up: this is the width they need
    // clientWidth is whole pixels and the measured width isn't, so a row that fits exactly keeps its names.
    if (need.current) setCompact(room < need.current - 0.5);
  }, [compact]);
  useLayoutEffect(measure);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [measure]);
  return [ref, compact];
}
// Each bar hands its own answer to its buttons, so Back and Snooze need nothing new passed in.
// Outside a measured bar a button is the icon alone, as every one of them was between #e5a0a527 and
// #194ccc7a.
const BarCompact = React.createContext(true);
// The group tints the bar has always worn (Calem, Bugify #c1ff895b, #7f0bc98f): the open mailbox's
// colour for Back, blue for Unread (the top menu's Quotation / Invoice / Receipt blue), red for
// Delete, green for Reply / Reply All / Forward, yellow for Spam / Archive / Snooze / Label. `on` is
// the deeper wash a button wears while its own menu is open.
type BarTint = { line: string; bg: string; fg: string; on?: string };
const BAR_TINT: Record<'accent' | 'blue' | 'red' | 'green' | 'amber', BarTint> = {
  accent: { line: 'color-mix(in srgb, var(--accent) 51%, transparent)', bg: 'color-mix(in srgb, var(--accent-bg) 50%, transparent)', fg: 'var(--accent)' },
  blue: { line: 'rgba(55,138,221,0.3)', bg: '#F7FBFE', fg: '#378ADD' },
  red: { line: 'rgba(226,75,74,0.3)', bg: '#FFF9F9', fg: '#E24B4A' },
  green: { line: 'rgba(21,128,61,0.25)', bg: '#F6FEF9', fg: '#15803D' },
  amber: { line: 'rgba(202,138,4,0.3)', bg: '#FFFEF4', fg: '#CA8A04', on: '#FFF9E3' },
};
// The square is a MINIMUM width, not a fixed one: in the icon row the button the pointer is on grows
// out of it into a pill (BarButton below), and a width that follows its own content is the only one
// both halves of that — growing and shrinking back — can animate; with the names up every button is
// that pill from the start. With the name away it is the square again, because an icon of BAR_ICON
// can never fill BAR_BTN. `overflow: hidden` keeps the name inside the outline while it is on its way
// in or out. `moving` false = the names are up, where there is nothing to animate (the pill is the
// resting shape and the pointer changes nothing) and an animation would be measured mid-slide.
function barBtn(tint: BarTint, open = false, moving = true): React.CSSProperties {
  return {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    minWidth: BAR_BTN, width: 'auto', height: BAR_BTN, flexShrink: 0, padding: 0, overflow: 'hidden',
    borderRadius: 'var(--radius-md)', border: `1px solid ${tint.line}`,
    background: open && tint.on ? tint.on : tint.bg, color: tint.fg, cursor: 'pointer',
    transition: moving ? 'padding 150ms ease' : 'none',
  };
}
const barIcon: React.CSSProperties = { fontSize: BAR_ICON, flexShrink: 0 };
// ── The button says its own name (Bugify #338c1651, #194ccc7a) ────────────────────
// When the row has room for all nine names it wears them: every button stands at rest as the pill —
// icon, BAR_LABEL_GAP, its name, BAR_PILL_PAD either side — and the pointer changes nothing
// ("if there is enough room, change them to their names", Bugify #194ccc7a).
// When it has not, icons alone are what keeps the row short, and their names were reachable only
// through the browser's own tooltip, which arrives a second late ("When you hover over the icons
// below, extend the hovered over icon to its full name. Example: Blue Arrow → Inbox, Blue Mail →
// Unread", Bugify #338c1651). So there the button under the pointer — or the one the keyboard has
// tabbed to — grows from its square into that same pill, and every other button stays exactly as it
// is: the row is ever one name wider, never nine, so it cannot outgrow the space beside the tabs.
// The name is the very string the button already carried as its label, nothing new is said, and it
// is read out of src/i18n so it speaks the staff member's own language.
// The name rides in a nowrap span whose max width goes 0 → its own width; it and the icon both keep
// flexShrink: 0, as everything in this row must (the bar measures its children — a child allowed to
// shrink would have the name cut instead of the pill widened).
const BAR_LABEL_GAP = 7;    // icon → name, inside the pill
const BAR_PILL_PAD = 9;     // the pill's own left/right padding: half the square's spare width
const BAR_LABEL_MAX = 150;  // a long one (a label's own name behind Back) stops here with an ellipsis
// Tabbing to a button opens its name as hovering does; a CLICK gives focus too, but it must not
// leave the name standing once the pointer has gone — :focus-visible is the line between the two.
function focusedByKeyboard(el: HTMLElement): boolean {
  try { return el.matches(':focus-visible'); } catch { return true; }   // a browser too old to know it
}
function BarButton({ tint, icon, label, title, open = false, onClick }: {
  tint: BarTint; icon: string; label: string; title?: string; open?: boolean; onClick: () => void;
}) {
  const compact = useContext(BarCompact);   // the row it stands in has no room for the names
  const [hovered, setHovered] = useState(false);
  const grown = !compact || hovered;        // names up: the pill at rest, and the pointer adds nothing
  return (
    <button
      onClick={onClick} title={title || label} aria-label={label}
      onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      onFocus={e => { if (focusedByKeyboard(e.currentTarget)) setHovered(true); }}
      onBlur={() => setHovered(false)}
      style={{ ...barBtn(tint, open, compact), padding: grown ? `0 ${BAR_PILL_PAD}px` : 0 }}
    >
      <i className={`ti ${icon}`} style={barIcon} aria-hidden />
      <span style={{
        flexShrink: 0, maxWidth: grown ? BAR_LABEL_MAX : 0, marginLeft: grown ? BAR_LABEL_GAP : 0,
        opacity: grown ? 1 : 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        fontSize: 12, fontWeight: 500, lineHeight: 1,
        transition: compact ? 'max-width 150ms ease, margin-left 150ms ease, opacity 120ms ease' : 'none',
      }}>{label}</span>
    </button>
  );
}
// A menu hanging off a bar button (Snooze, Label) opens BELOW it, right-aligned: in a conversation the
// bar ends at the pane's right edge, and in a single email the row runs towards it, so a menu reaching
// rightwards could run off it. In the column it opened to the button's right instead, because there
// the buttons were the pane's LEFT edge.
const barMenu: React.CSSProperties = {
  position: 'absolute', top: '100%', right: 0, marginTop: 6, background: 'var(--bg-surface)',
  border: '1px solid var(--border)', borderRadius: 8, boxShadow: 'var(--shadow-md)', padding: 6, zIndex: 50,
};

// "← Inbox" — split screen off only, where the reading pane replaces the list. Its tooltip names the
// folder / label the list is showing, since that is where the button goes — the name alone, without
// "Back to" (Calem, Bugify #c1ff895b). Shared by both views.
// It wears the open mailbox's colour the way the sidebar's Sync bubble does — a pale wash, a thin
// outline of the colour itself — through the module's --accent, so it is teal while no mailbox is
// open, like the floating "+". Calem (Bugify #e0e30d4b) asked for it in place of plain grey.
// The wash is half the Sync bubble's: two shades paler, like the rest of the row (Calem, Bugify #c1ff895b).
// Its outline is the colour at 51%, where it was 35%: a shade darker, as the list's search bubble went (Calem, report
// 0fb815da: "Make the bubble border one shade darker for everything on the purple menu pages" — see EmailList).
// It heads the action bar, so it says the folder's name when the row has room for every name
// (Bugify #194ccc7a) and is the arrow alone when it has not — the same small square as every other
// button in the row, the name kept as its tooltip (Calem, Bugify #e5a0a527) and said in the pill the
// arrow grows into while the pointer is on it ("Blue Arrow → Inbox", Bugify #338c1651).
// A long one (one of Gmail's own labels) stops at BAR_LABEL_MAX with an ellipsis, so it can never be
// the button that drives the whole row down to icons.
function BackButton({ onBack }: { onBack: () => void }) {
  const { t } = useTranslation();
  const { activeFolder, activeAccountId, labelOverrides } = useStore();
  const folder = FOLDER_NAMES[activeFolder] || (activeFolder === 'spam' ? t('spam') : activeFolder === 'snoozed' ? t('eml_snoozed') : '') || labelOverrides[activeAccountId || '']?.[activeFolder]?.name || activeFolder || 'Inbox';
  const label = t('eml_back_to_list', { folder });
  return (
    <BarButton tint={BAR_TINT.accent} icon="ti-arrow-left" label={label} onClick={onBack} />
  );
}

// ── Sender → Contacts (Calem, Bugify #1b459002) ──────────────────────────────
const addrKey = (a: string) => (a || '').trim().toLowerCase();

// Which of these sender addresses are already on a card in Contacts. /contacts/search matches name OR
// address loosely, and a card can hold several addresses ("a@x.com, b@y.com"), so only the exact
// address on a card counts. An address still being looked up — or whose lookup failed — is absent.
function useSavedContacts(addresses: string[]) {
  const [saved, setSaved] = useState<Record<string, boolean>>({});
  const asked = useRef<Set<string>>(new Set());
  const want = Array.from(new Set(addresses.map(addrKey).filter(Boolean))).sort().join('\n');
  useEffect(() => {
    for (const a of want ? want.split('\n') : []) {
      if (asked.current.has(a)) continue;
      asked.current.add(a);
      api.searchContacts(a).then((rows: any) => {
        const yes = (Array.isArray(rows) ? rows : []).some((c: any) => String(c.email || '').split(/[,;]/).some(x => addrKey(x) === a));
        setSaved(s => ({ ...s, [a]: yes }));
      }).catch(() => { asked.current.delete(a); });
    }
  }, [want]);
  const markSaved = useCallback((a: string) => setSaved(s => ({ ...s, [addrKey(a)]: true })), []);
  return { saved, markSaved };
}

// Right after the sender's <address>: a small round bubble that opens a NEW Contacts card already
// holding the sender's name and address — or, once that address is on a card, a green check. Worn
// like the sidebar's Sync bubble (the open mailbox's colour). Nothing shows while the lookup is out.
// Its outline is the colour at 51%, a shade darker than the 35% it was, as the Back button's is (Calem,
// report 0fb815da). The green check keeps its own.
function ContactBubble({ saved, onAdd }: { saved: boolean | undefined; onAdd: () => void }) {
  const { t } = useTranslation();
  if (saved === undefined) return null;
  const round: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 18, height: 18, padding: 0, marginLeft: 6, borderRadius: '50%', verticalAlign: 'middle', flexShrink: 0 };
  if (saved) return (
    <span title={t('eml_contact_exists')} style={{ ...round, border: '1px solid rgba(21,128,61,0.25)', background: '#DCFCE7', color: '#15803D' }}>
      <i className="ti ti-check" style={{ fontSize: 11 }} aria-hidden />
    </span>
  );
  return (
    <button onClick={e => { e.stopPropagation(); onAdd(); }} title={t('eml_contact_add')}
      style={{ ...round, border: '1px solid color-mix(in srgb, var(--accent) 51%, transparent)', background: 'var(--accent-bg)', color: 'var(--accent)', cursor: 'pointer' }}>
      <i className="ti ti-user-plus" style={{ fontSize: 11 }} aria-hidden />
    </button>
  );
}

// ── Snooze (Calem, approved) ─────────────────────────────────────────────────
// Times are the tenant's wall clock (utils/region.ts) whatever zone the computer's clock is in.
// The helpers live in utils/bkkTime.ts (shared with the composers' Schedule send menu).
// Later today 18:00 (only before 18:00) · Tomorrow 08:00 · This weekend Sat 08:00 (Mon–Fri only) ·
// Next week Mon 08:00.
function snoozePresets(): { key: string; at: number }[] {
  const { dow, hour } = bkkNow();
  const out: { key: string; at: number }[] = [];
  if (hour < 18) out.push({ key: 'eml_snooze_later_today', at: bkkAt(0, 18) });
  const tomorrow = bkkAt(1, 8);
  out.push({ key: 'eml_snooze_tomorrow', at: tomorrow });
  // On a Friday "This weekend" (Saturday 08:00) IS tomorrow, and on a Sunday "Next week" (Monday
  // 08:00) is — one entry per moment, never two rows with the same time.
  const weekend = bkkAt(6 - dow, 8);
  if (dow >= 1 && dow <= 5 && weekend !== tomorrow) out.push({ key: 'eml_snooze_weekend', at: weekend });
  const nextWeek = bkkAt((8 - dow) % 7 || 7, 8);
  if (nextWeek !== tomorrow) out.push({ key: 'eml_snooze_next_week', at: nextWeek });
  return out;
}

// "Snooze until…" — sits after Archive in the action bar, on Inbox emails of the user's own mailbox
// only (never a shared / role one like a firm's public address). Inside Snoozed it also offers
// Unsnooze. `ids` = the email, or a conversation's Inbox messages, snoozed together. `canSet` false =
// the owner of a mailbox that is no longer personal (shared since): only Unsnooze is offered.
// In the bar it is the clock with its name beside it, or — where the row has no room for the names —
// the clock alone, a small square with its label as its tooltip; its 260px menu hangs below the
// button, right-aligned either way (Calem, Bugify #e5a0a527, #194ccc7a).
function SnoozeButton({ ids, canSet = true }: { ids: string[]; canSet?: boolean }) {
  const { t } = useTranslation();
  const { uiLang, activeFolder, snoozeEmails, showToast } = useStore();
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState(false);
  const [value, setValue] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const toggle = () => { setOpen(v => !v); setCustom(false); setErr(''); setValue(toBkkInput(bkkAt(1, 8))); };
  // at = ms, or null to unsnooze.
  const run = async (at: number | null) => {
    if (busy) return;
    if (at !== null && !(at > Date.now())) { setErr(t('eml_snooze_past')); return; }
    setBusy(true);
    const ok = await snoozeEmails(ids, at === null ? null : Math.floor(at / 1000));
    setBusy(false);
    setOpen(false);
    showToast(!ok ? t('eml_snooze_failed') : at === null ? t('eml_snooze_unsnoozed') : t('eml_snooze_done', { time: fmtBkk(at, uiLang) }));
  };
  const rowStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '7px 9px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 12.5, color: 'var(--text-primary)', borderRadius: 6, textAlign: 'left' };
  const hover = { onMouseEnter: (e: React.MouseEvent<HTMLButtonElement>) => (e.currentTarget.style.background = 'var(--bg-active)'), onMouseLeave: (e: React.MouseEvent<HTMLButtonElement>) => (e.currentTarget.style.background = 'transparent') };

  return (
    <div ref={boxRef} style={{ position: 'relative', flexShrink: 0 }}>
      <BarButton tint={BAR_TINT.amber} icon="ti-clock-pause" label={t('eml_snooze')} open={open} onClick={toggle} />
      {/* Below the button and right-aligned, like the Label menu: the bar ends at (in a conversation)
          or runs towards (in a single email) the pane's right edge, so a menu reaching rightwards
          could run off it. */}
      {open && (
        <div style={{ ...barMenu, minWidth: 260 }}>
          {canSet && <div style={{ padding: '4px 9px 6px', fontSize: 11.5, fontWeight: 600, color: 'var(--text-tertiary)' }}>{t('eml_snooze_until')}</div>}
          {canSet && snoozePresets().map(p => (
            <button key={p.key} disabled={busy} onClick={() => run(p.at)} style={rowStyle} {...hover}>
              <span style={{ flex: 1 }}>{t(p.key)}</span>
              <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)', whiteSpace: 'nowrap' }}>{fmtBkk(p.at, uiLang)}</span>
            </button>
          ))}
          {canSet && <div style={{ borderTop: '1px solid var(--border)', margin: '4px 0' }} />}
          {canSet && <button disabled={busy} onClick={() => { setCustom(v => !v); setErr(''); }} style={rowStyle} {...hover}>
            <i className="ti ti-calendar-event" style={{ fontSize: 13, color: 'var(--text-tertiary)', width: 14 }} aria-hidden />
            <span style={{ flex: 1 }}>{t('eml_snooze_custom')}</span>
          </button>}
          {canSet && custom && (
            <div style={{ padding: '4px 9px 6px', display: 'flex', flexDirection: 'column', gap: 6 }}>
              <input type="datetime-local" value={value} min={toBkkInput(Date.now())} onChange={e => { setValue(e.target.value); setErr(''); }}
                style={{ padding: '6px 8px', fontSize: 12.5, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', color: 'var(--text-primary)' }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ flex: 1, fontSize: 11, color: err ? '#E24B4A' : 'var(--text-tertiary)' }}>{err || t('eml_snooze_bkk')}</span>
                <button disabled={busy || !value} onClick={() => run(fromBkkInput(value))} style={{ padding: '5px 12px', fontSize: 12, fontWeight: 600, borderRadius: 'var(--radius-md)', border: 'none', background: 'var(--accent)', color: '#fff', cursor: 'pointer', opacity: busy || !value ? 0.6 : 1 }}>
                  {t('eml_snooze')}
                </button>
              </div>
            </div>
          )}
          {activeFolder === 'snoozed' && (<>
            {canSet && <div style={{ borderTop: '1px solid var(--border)', margin: '4px 0' }} />}
            <button disabled={busy} onClick={() => run(null)} style={rowStyle} {...hover}>
              <i className="ti ti-clock-play" style={{ fontSize: 13, color: 'var(--text-tertiary)', width: 14 }} aria-hidden />
              <span style={{ flex: 1 }}>{t('eml_snooze_unsnooze')}</span>
            </button>
          </>)}
        </div>
      )}
    </div>
  );
}

type ComposeMode = 'reply' | 'replyAll' | 'forward' | null;
type ActiveTab = 'original' | 'translation';

export default function EmailDetail({ onBack }: { onBack?: () => void } = {}) {
  const { t } = useTranslation();
  const { uiLang, toggleStar, deleteEmail, archiveEmail, moveToSpam, setLabel, markUnread, getActiveEmail, translateEmail, showToast, setActiveEmail, me, folders, labelOverrides, loadEmails, accounts, activeFolder, replyAllDefault } = useStore();
  const email = getActiveEmail();
  const canLead = me?.role === 'admin' || !!me?.modules?.includes('leads');

  const [activeTab, setActiveTab] = useState<ActiveTab>('original');
  const [composeMode, setComposeMode] = useState<ComposeMode>(null);
  const [addLead, setAddLead] = useState(false);
  const [showLabels, setShowLabels] = useState(false);
  const [confirm, setConfirm] = useState<{ msg: string; action: () => void } | null>(null);
  // The sender's "Add to contacts" bubble — a single email's here; a conversation's in ThreadView.
  const { saved: savedContacts, markSaved } = useSavedContacts(email && (email.threadCount || 1) <= 1 ? [email.from.email] : []);
  const [addContact, setAddContact] = useState<{ name: string; email: string } | null>(null);
  // The picture in the body whose wand was clicked — Rectify (AI) (report 990a6f19, opening #3).
  const [bodyPic, setBodyPic] = useState<{ emailId: string; n: number } | null>(null);
  // The action bar beside the tabs: icons alone while the space beside them is too narrow for the names.
  const [barRef, barCompact] = useCompactBar();

  // Translation state — loaded lazily when user clicks the translation tab
  const [translatedBody, setTranslatedBody] = useState<string | null>(null);
  const [translating, setTranslating] = useState(false);
  const [translateError, setTranslateError] = useState(false);

  // Reset state when email changes
  useEffect(() => {
    setActiveTab('original');
    setComposeMode(null);
    setTranslatedBody(null);
    setTranslating(false);
    setTranslateError(false);
    setShowLabels(false);
  }, [email?.id]);

  // Lazy-load translation only when translation tab is first clicked
  const handleTranslationTab = useCallback(() => {
    setActiveTab('translation');
    if (!email) return;
    // Same language — nothing to translate
    if (email.lang === uiLang) return;
    // Already loaded or loading
    if (translatedBody !== null || translating) return;
    // Check in-memory cache first
    const cached = email.bodyTranslated[uiLang];
    // Skip stale/broken cached translations
    if (cached && !cached.includes('[No API key configured]')) { setTranslatedBody(cached); return; }
    // Fetch from server
    setTranslating(true);
    setTranslateError(false);
    translateEmail(email.id, uiLang)
      .then(result => setTranslatedBody(result || ''))
      .catch(() => setTranslateError(true))
      .finally(() => setTranslating(false));
  }, [email, uiLang, translatedBody, translating, translateEmail]);

  // Re-trigger translation if user switches UI language while on translation tab
  useEffect(() => {
    if (activeTab === 'translation' && email && email.lang !== uiLang) {
      setTranslatedBody(null);
      setTranslating(false);
      setTranslateError(false);
      const cached = email.bodyTranslated[uiLang];
      if (cached && !cached.includes('[No API key configured]')) { setTranslatedBody(cached); return; }
      setTranslating(true);
      translateEmail(email.id, uiLang)
        .then(r => setTranslatedBody(r || ''))
        .catch(() => setTranslateError(true))
        .finally(() => setTranslating(false));
    }
  }, [uiLang]);

  if (!email) {
    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-surface)' }}>
        <i className="ti ti-mail-opened" style={{ fontSize: 48, color: 'var(--text-tertiary)', marginBottom: 12 }} aria-hidden />
        <div style={{ fontSize: 14, color: 'var(--text-secondary)' }}>{t('noEmailSelected')}</div>
      </div>
    );
  }

  // Conversations (2+ messages) render the Gmail-style thread view.
  if ((email.threadCount || 1) > 1) return <ThreadView key={email.threadId || email.id} root={email} onBack={onBack} />;

  const langName = (code: string) => t(`langNames.${code}` as any, { defaultValue: code });
  const canTranslate = email.lang !== uiLang;

  const handleDelete = () => setConfirm({
    msg: email.folder === 'trash'
      ? 'Permanently delete this email? This cannot be undone.'
      : 'Move this email to Trash?',
    action: () => {
      setConfirm(null);
      void deleteEmail(email.id);   // the row leaves at once; a failure brings it back with its own toast
      showToast(email.folder === 'trash' ? 'Email permanently deleted' : 'Moved to Trash');
    },
  });

  const handleArchive = () => setConfirm({
    msg: 'Archive this email? It will be moved out of your inbox.',
    action: async () => {
      setConfirm(null);
      await archiveEmail(email.id);
      showToast('Archived');
    },
  });

  const handleSpam = async () => { await moveToSpam(email.id); showToast('Moved to Spam'); };

  // Custom labels of this email's account (from the synced folder list), with the local
  // rename/colour applied and labels deleted in PandaAI Email left out.
  const acctOverrides = labelOverrides[email.accountId];
  const acctLabels = ((folders as any)[email.accountId] || []).filter((f: any) => f.kind === 'label' && !labelView(f, acctOverrides).hidden);
  const snoozeAcct = accounts.find(a => a.id === email.accountId);
  // Set a snooze: own PERSONAL mailbox only. Unsnooze (inside Snoozed): the mailbox's owner, even if it
  // has since been shared — otherwise a snoozed email stays hidden from everyone until the cron.
  const canSnooze = email.folder === 'inbox' && !!snoozeAcct?.personal;
  const canUnsnooze = activeFolder === 'snoozed' && !!snoozeAcct && !snoozeAcct.shared;

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: 'var(--bg-surface)' }} key={email.id}>

      {/* ── Header ── */}
      <div style={{ padding: '14px 20px 0', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
        {/* Subject + meta row */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 8 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1.4, marginBottom: 8 }}>{email.subject}</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {/* The sender's initials, trimmed to their cap height so the flex box centres the capitals themselves (Bugify #836d4443, 2nd opening) */}
              <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--accent-bg)', color: 'var(--accent-text)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
                <span style={{ textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic' }}>{(email.from.name || email.from.email).split(' ').map((w: string) => w[0]).join('').slice(0, 2).toUpperCase()}</span>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{email.from.name || email.from.email}</div>
                <div style={{ display: 'flex', alignItems: 'center', minHeight: 18, fontSize: 11, color: 'var(--text-tertiary)' }}>
                  &lt;{email.from.email}&gt;
                  <ContactBubble saved={savedContacts[addrKey(email.from.email)]} onAdd={() => setAddContact({ name: email.from.name || '', email: email.from.email })} />
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                {email.lang && (
                  <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 'var(--radius-md)', background: 'var(--c-gray-100)', color: 'var(--text-secondary)', fontWeight: 500, border: '1px solid var(--border)' }}>
                    {langName(email.lang)}
                  </span>
                )}
                <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{format(email.date, 'dd/MM/yyyy · HH:mm')}</span>
                <button onClick={() => toggleStar(email.id)} title={email.starred ? 'Unstar' : 'Star'} style={{ width: 28, height: 28, border: 'none', background: 'transparent', color: email.starred ? '#f5a623' : 'var(--text-tertiary)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 'var(--radius-sm)' }}>
                  <i className={`ti ti-star${email.starred ? '-filled' : ''}`} style={{ fontSize: 15 }} aria-hidden />
                </button>
                {canLead && (
                  <button onClick={() => setAddLead(true)} title={email.lead ? t('ld_change_or_remove') : t('ld_add_to')} style={{ width: 28, height: 28, border: 'none', background: 'transparent', color: '#6D28D9', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 'var(--radius-sm)' }}>
                    <i className="ti ti-target-arrow" style={{ fontSize: 15 }} aria-hidden />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
        {/* To / CC */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, paddingLeft: 42, marginBottom: 10 }}>
          {email.to.length > 0 && <div style={{ fontSize: 12 }}><span style={{ color: 'var(--text-tertiary)', fontWeight: 500 }}>To: </span><span style={{ color: 'var(--text-secondary)' }}>{email.to.join(', ')}</span></div>}
          {email.cc.length > 0 && <div style={{ fontSize: 12 }}><span style={{ color: 'var(--text-tertiary)', fontWeight: 500 }}>CC: </span><span style={{ color: 'var(--text-secondary)' }}>{email.cc.join(', ')}</span></div>}
        </div>

        {/* ── Tabs, and the action buttons in the free space beside them (Calem, Bugify #e5a0a527,
            screenshot-2 and screenshot-4) — one row, the tabs at its left and the icons straight after
            them: the row's 20px gap is the space between the Translation tab and the first icon, and
            the icons stand 10px above the header's bottom line ("left align (20px) away from the
            original/translation tabs" and "bring them up 10px from the gray line", Bugify #3146d7e0).
            The tabs sit on that line (the -1px margin lets the open tab cover it and join the content
            below); alignItems flex-end keeps them down there while the lifted icons make the row
            taller. ── */}
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 20, marginBottom: -1 }}>
          <div style={{ display: 'flex', gap: 0, flexShrink: 0 }}>
            {/* Original tab */}
            <button
              onClick={() => setActiveTab('original')}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '7px 16px', fontSize: 12, fontWeight: activeTab === 'original' ? 600 : 400,
                border: '1px solid var(--border)', borderBottom: activeTab === 'original' ? '1px solid var(--bg-surface)' : '1px solid var(--border)',
                borderRadius: 'var(--radius-md) var(--radius-md) 0 0',
                background: activeTab === 'original' ? 'var(--bg-surface)' : 'var(--c-gray-50)',
                color: activeTab === 'original' ? 'var(--text-primary)' : 'var(--text-secondary)',
                cursor: 'pointer', marginRight: 2, position: 'relative', zIndex: activeTab === 'original' ? 1 : 0,
              }}
            >
              <i className="ti ti-mail" style={{ fontSize: 13 }} aria-hidden />
              Original
              {email.lang && (
                <span style={{ fontSize: 10, padding: '1px 5px', borderRadius: 8, background: 'var(--c-gray-100)', color: 'var(--text-tertiary)', fontWeight: 500 }}>
                  {email.lang.toUpperCase()}
                </span>
              )}
            </button>

            {/* Translation tab */}
            <button
              onClick={handleTranslationTab}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '7px 16px', fontSize: 12, fontWeight: activeTab === 'translation' ? 600 : 400,
                border: '1px solid var(--border)', borderBottom: activeTab === 'translation' ? '1px solid var(--bg-surface)' : '1px solid var(--border)',
                borderRadius: 'var(--radius-md) var(--radius-md) 0 0',
                background: activeTab === 'translation' ? 'var(--bg-surface)' : 'var(--c-gray-50)',
                color: activeTab === 'translation' ? 'var(--accent)' : canTranslate ? 'var(--text-secondary)' : 'var(--text-tertiary)',
                cursor: 'pointer', position: 'relative', zIndex: activeTab === 'translation' ? 1 : 0,
              }}
            >
              <i className="ti ti-language" style={{ fontSize: 13, color: activeTab === 'translation' ? 'var(--accent)' : 'inherit' }} aria-hidden />
              Translation
              <span style={{ fontSize: 10, padding: '1px 5px', borderRadius: 8, background: activeTab === 'translation' ? 'var(--accent-bg)' : 'var(--c-gray-100)', color: activeTab === 'translation' ? 'var(--accent-text)' : 'var(--text-tertiary)', fontWeight: 500, border: activeTab === 'translation' ? '1px solid color-mix(in srgb, var(--accent) 20%, transparent)' : 'none' }}>
                {uiLang.toUpperCase()}
              </span>
              {translating && <i className="ti ti-loader-2" style={{ fontSize: 11, color: 'var(--accent)', animation: 'spin 1s linear infinite' }} aria-hidden />}
            </button>
          </div>

          {/* ── Action bar — in the free space to the right of the tabs: Back to the folder
              (split screen off only, where the reading pane replaces the list) · Unread (it marks the
              email unread; "Mark" left off the label, Calem, Bugify #c1ff895b) · Delete · Reply /
              Reply All / Forward · Spam · Archive · Snooze · Label. While a reply is in progress only
              Back stays. Each button keeps its group's tint and carries its label as its tooltip.
              This div is the row whose width the names are judged against: `flex: 1` makes it exactly
              the room left beside the tabs, so when all the names fit in it every button wears its
              own (Bugify #194ccc7a) and when they do not it is the line of small squares, each saying
              its name in the pill it grows into under the pointer. Left-aligned, not at the pane's
              right end; the 11px bottom margin leaves 10px clear above the grey line, because the
              row's -1px margin takes up the other 1px (Bugify #3146d7e0). ── */}
          {(onBack || !composeMode) && (
            <div ref={barRef} style={{ ...barStyle, flex: 1, minWidth: 0, justifyContent: 'flex-start', marginBottom: 11, flexWrap: barCompact ? 'wrap' : 'nowrap' }}>
              <BarCompact.Provider value={barCompact}>
              {onBack && <BackButton onBack={onBack} />}
              {!composeMode && (<>
                {/* Unread — marks it unread and closes the reading pane, so the list shows it as unread again */}
                <BarButton tint={BAR_TINT.blue} icon="ti-mail" label={t('eml_mark_unread')}
                  onClick={() => { markUnread(email.id); showToast('Marked as unread'); setActiveEmail(null); }} />
                <BarButton tint={BAR_TINT.red} icon="ti-trash" label={t('delete')} onClick={handleDelete} />
                {/* No Reply while the Reply all switch is on — it would only open as Reply All (report 643e2141) */}
                {([
                  { key: 'reply', icon: 'ti-corner-up-left' },
                  { key: 'replyAll', icon: 'ti-corner-up-left-double' },
                  { key: 'forward', icon: 'ti-corner-up-right' },
                ] as const).filter(({ key }) => key !== 'reply' || !replyAllDefault).map(({ key, icon }) => (
                  <BarButton key={key} tint={BAR_TINT.green} icon={icon} label={t(key)}
                    onClick={() => setComposeMode(replyModeOf(key))} />
                ))}
                <BarButton tint={BAR_TINT.amber} icon="ti-alert-octagon" label={t('spam')} title={t('eml_bar_spam_tip')} onClick={handleSpam} />
                <BarButton tint={BAR_TINT.amber} icon="ti-archive" label={t('archive')} onClick={handleArchive} />
                {(canSnooze || canUnsnooze) && <SnoozeButton ids={[email.id]} canSet={canSnooze} />}
                {acctLabels.length > 0 && (
                  <div style={{ position: 'relative', flexShrink: 0 }}>
                    <BarButton tint={BAR_TINT.amber} icon="ti-tag" label={t('eml_bar_label')} open={showLabels}
                      onClick={() => setShowLabels(v => !v)} />
                    {/* Right-aligned under the button: Label ends the row, so a left-aligned menu could
                        run past the pane's edge and be cut off. */}
                    {showLabels && (
                      <div style={{ ...barMenu, minWidth: 210, maxHeight: 260, overflowY: 'auto' }}>
                        {acctLabels.map((l: any) => {
                          const has = email.labels.includes(l.path);
                          const v = labelView(l, acctOverrides);
                          return (
                            <button key={l.path} onClick={() => setLabel(email.id, l.path, !has)} style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '7px 9px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 12.5, color: 'var(--text-primary)', borderRadius: 6, textAlign: 'left' }}
                              onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-active)')} onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                              <i className={`ti ti-${has ? 'check' : 'plus'}`} style={{ fontSize: 13, color: has ? '#15803D' : 'var(--text-tertiary)', width: 14 }} aria-hidden />
                              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: v.color || undefined }}>{v.name}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </>)}
              </BarCompact.Provider>
            </div>
          )}
        </div>
      </div>

      {/* ── Tab content ── */}
      <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>

        {/* Original tab content */}
        {activeTab === 'original' && (
          <>
            <div style={{ padding: '16px 20px 0' }}>
              <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', overflow: 'hidden', background: '#fff' }}>
                <HtmlFrame html={email.bodyOriginal} onRectifyImage={n => setBodyPic({ emailId: email.id, n })} />
              </div>
            </div>
            <AttachmentsPanel emailId={email.id} attachments={email.attachments as any[]} />
            <div style={{ height: FAB_ROOM }} />
          </>
        )}

        {/* Translation tab content */}
        {activeTab === 'translation' && (
          <>
            <div style={{ padding: '16px 20px 0' }}>
              {!canTranslate ? (
                /* Same language — no translation needed */
                <div style={{ padding: '40px 24px', textAlign: 'center' }}>
                  <i className="ti ti-language-off" style={{ fontSize: 36, color: 'var(--text-tertiary)', marginBottom: 12, display: 'block' }} aria-hidden />
                  <div style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 4 }}>No translation needed</div>
                  <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                    The email is already in <b>{langName(uiLang)}</b> — the same as your selected language.
                  </div>
                </div>
              ) : translating ? (
                /* Loading */
                <div style={{ padding: '40px 24px', textAlign: 'center' }}>
                  <i className="ti ti-loader-2" style={{ fontSize: 36, color: 'var(--accent)', marginBottom: 12, display: 'block', animation: 'spin 1s linear infinite' }} aria-hidden />
                  <div style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 4 }}>Translating…</div>
                  <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                    Translating from <b>{langName(email.lang)}</b> to <b>{langName(uiLang)}</b>
                  </div>
                </div>
              ) : translateError ? (
                /* Error */
                <div style={{ padding: '40px 24px', textAlign: 'center' }}>
                  <i className="ti ti-alert-circle" style={{ fontSize: 36, color: '#E24B4A', marginBottom: 12, display: 'block' }} aria-hidden />
                  <div style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 12 }}>Translation failed</div>
                  <button onClick={handleTranslationTab} style={{ padding: '7px 16px', fontSize: 12, borderRadius: 'var(--radius-md)', border: '1px solid var(--accent)', background: 'transparent', color: 'var(--accent)', cursor: 'pointer', fontWeight: 500 }}>
                    Try again
                  </button>
                </div>
              ) : translatedBody !== null ? (
                /* Translated content */
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 8 }}>
                    <i className="ti ti-language" style={{ fontSize: 12, color: 'var(--accent)' }} aria-hidden />
                    <span>Translated from <b style={{ color: 'var(--text-secondary)' }}>{langName(email.lang)}</b> to <b style={{ color: 'var(--accent)' }}>{langName(uiLang)}</b></span>
                  </div>
                  <div style={{ border: '1px solid color-mix(in srgb, var(--accent) 25%, transparent)', borderRadius: 'var(--radius-md)', overflow: 'hidden', background: '#fff' }}>
                    <HtmlFrame html={translatedBody} />
                  </div>
                </div>
              ) : null}
            </div>
            <AttachmentsPanel emailId={email.id} attachments={email.attachments as any[]} />
            <div style={{ height: FAB_ROOM }} />
          </>
        )}
      </div>

      {/* ── Compose panel ── */}
      {composeMode && (
        <Composer email={email} mode={composeMode} onClose={() => setComposeMode(null)} />
      )}

      {/* ── Confirm dialog ── */}
      {confirm && <ConfirmDialog message={confirm.msg} onConfirm={confirm.action} onCancel={() => setConfirm(null)} />}
      {addLead && email && <AddToLeadModal email={{ id: email.id, subject: email.subject, fromName: email.from.name || '', fromEmail: email.from.email || '', date: email.date, bodyText: email.bodyOriginal || '' }}
        current={email.lead ? { rowId: email.lead.rowId, id: email.lead.id, name: email.lead.name, company: email.lead.company } : undefined}
        onClose={() => setAddLead(false)} onDone={() => { showToast(t('ld_added')); void loadEmails({ silent: true } as any); }} />}
      {addContact && <ContactForm contact={null} initial={addContact} onClose={() => setAddContact(null)}
        onSaved={() => { if (!savedContacts[addrKey(addContact.email)]) showToast(t('eml_contact_added')); markSaved(addContact.email); }} />}
      {bodyPic && <RectifyModal key={`${bodyPic.emailId}:${bodyPic.n}`} doors={doorsOf(bodyPic.emailId, { image: bodyPic.n })} onClose={() => setBodyPic(null)} />}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}


// ── Conversation view (Gmail-style) ──────────────────────────────────────────
// All messages of the thread, oldest→newest, fetched WITH bodies in ONE request — expanding a
// message is pure UI (nothing can hang). Older ones collapse to one-line headers; the newest is
// expanded by default. Messages are marked read as the user expands them, and whatever is still
// unread once they leave the conversation (Back, or opening another email from the list).
function ThreadView({ root, onBack }: { root: Email; onBack?: () => void }) {
  const { t } = useTranslation();
  const { uiLang, deleteEmails, archiveEmail, setActiveEmail, showToast, setStar, translateEmail, me, loadEmails, markUnread, accounts, activeFolder, replyAllDefault } = useStore();
  // Conversations are most of the inbox, so this is the PRIMARY place staff assign / fix a lead.
  const canLead = me?.role === 'admin' || !!me?.modules?.includes('leads');
  const [addLead, setAddLead] = useState(false);
  const [msgs, setMsgs] = useState<any[]>([]);
  const [loadErr, setLoadErr] = useState(false);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [showTrans, setShowTrans] = useState<Set<string>>(new Set());
  const [transBusy, setTransBusy] = useState<Set<string>>(new Set());
  const [transBody, setTransBody] = useState<Record<string, string>>({});
  const [compose, setCompose] = useState<{ email: Email; mode: 'reply' | 'replyAll' | 'forward' } | null>(null);
  const [confirm, setConfirm] = useState<{ msg: string; action: () => void } | null>(null);
  const [reloadTick, setReloadTick] = useState(0);
  const [totalCount, setTotalCount] = useState(0);   // server-side total (endpoint caps the payload)
  // The message the top Reply/Reply All/Forward bar acts on — follows whichever message the
  // user most recently expanded (defaults to the latest).
  const [replyTargetId, setReplyTargetId] = useState<string | null>(null);
  // "Add to contacts" beside the sender in each open message's header. Its form sits at the root, not
  // in the header — a click inside it would reach the header and fold the message away.
  const { saved: savedContacts, markSaved } = useSavedContacts(msgs.filter(m => expanded.has(m.id)).map(m => m.from_email || ''));
  const [addContact, setAddContact] = useState<{ name: string; email: string } | null>(null);
  // The picture in a message's body whose wand was clicked — Rectify (AI) (report 990a6f19, opening #3).
  const [bodyPic, setBodyPic] = useState<{ emailId: string; n: number } | null>(null);
  // The action bar on the header's top line: icons alone while that line is too narrow for the names.
  const [barRef, barCompact] = useCompactBar();
  const scrollRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const lastIdRef = useRef<string>('');
  // Land on the TOP of the latest message (its header), not its bottom — the user reads from
  // the beginning of the newest email.
  const scrollToLatest = useCallback(() => {
    const el = lastIdRef.current ? cardRefs.current[lastIdRef.current] : null;
    if (el) el.scrollIntoView({ block: 'start' });
    else if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, []);

  const msgsRef = useRef<any[]>([]);
  const expandedRef = useRef<Set<string>>(new Set());
  useEffect(() => { expandedRef.current = expanded; }, [expanded]);
  const markedRef = useRef<Set<string>>(new Set());   // ids already marked read (guards double-effects)

  const markExpandedRead = useCallback((m: any) => {
    if (!m.unread || markedRef.current.has(m.id)) return;
    markedRef.current.add(m.id);
    api.patchEmail(m.id, { unread: false })   // best-effort; list reconciles on refresh
      .then(() => useStore.getState().loadLabelUnread()).catch(() => {});
    setMsgs(ms => ms.map(x => x.id === m.id ? { ...x, unread: 0 } : x));
    // Reflect it on the list row's aggregate so the conversation stops showing as unread.
    useStore.setState(s => ({
      emails: s.emails.map(r => {
        if (!(r.threadIds || []).includes(m.id)) return r;
        const left = Math.max(0, (r.threadUnread || 0) - 1);
        return { ...r, threadUnread: left, unread: left > 0 };
      }),
    }));
  }, []);

  // Leaving the conversation marks every message still unread in it read, not just the expanded
  // ones — otherwise its row stays unread (and tinted) back in the list.
  const markAllRead = useCallback(() => { msgsRef.current.forEach(markExpandedRead); }, [markExpandedRead]);
  useEffect(() => () => {
    // Unmounting with a different email open = the user went straight to it from the list. Back
    // marks on its own click; delete / archive clear the open email, so they mark nothing.
    const now = useStore.getState().getActiveEmail();
    if (now && (now.threadId || now.id) !== (root.threadId || root.id)) markAllRead();
  }, []); // eslint-disable-line

  useEffect(() => {
    let alive = true;
    const firstLoad = msgsRef.current.length === 0;
    if (firstLoad) { setLoading(true); setLoadErr(false); }
    api.getThread(root.threadId || root.id, root.accountId).then((d: any) => {
      if (!alive) return;
      const rows = d.emails || [];
      const prevIds = new Set(msgsRef.current.map((x: any) => x.id));
      msgsRef.current = rows;
      setMsgs(rows); setTotalCount(d.total || rows.length); setLoading(false); setLoadErr(false);
      const last = rows[rows.length - 1];
      if (prevIds.size === 0) {
        if (last) { lastIdRef.current = last.id; setExpanded(new Set([last.id])); setReplyTargetId(last.id); if (last.unread) markExpandedRead(last); }
        // Land on the latest message; re-run after the body iframes finish measuring their height.
        setTimeout(scrollToLatest, 50); setTimeout(scrollToLatest, 400); setTimeout(scrollToLatest, 1300);
      } else {
        // Background refetch (e.g. a new reply arrived): keep whatever the user expanded,
        // and additionally expand a genuinely new latest message.
        const keep = new Set(Array.from(expandedRef.current).filter(id => rows.some((r: any) => r.id === id)));
        if (last && !prevIds.has(last.id)) { keep.add(last.id); setReplyTargetId(last.id); if (last.unread) markExpandedRead(last); }
        setExpanded(keep);
      }
    }).catch(() => { if (alive && firstLoad) { setLoading(false); setLoadErr(true); } });
    return () => { alive = false; };
  }, [root.threadId, root.id, reloadTick]); // eslint-disable-line

  const toggle = (m: any) => {
    const opening = !expandedRef.current.has(m.id);
    if (opening) markExpandedRead(m);
    setExpanded(prev => {
      const n = new Set(prev);
      if (n.has(m.id)) n.delete(m.id); else n.add(m.id);
      return n;
    });
    if (opening) {
      setReplyTargetId(m.id);
      const into = () => cardRefs.current[m.id]?.scrollIntoView({ block: 'nearest' });
      setTimeout(into, 30); setTimeout(into, 400);   // again once the body iframe has measured
    } else if (replyTargetId === m.id) {
      const lastMsg = msgsRef.current[msgsRef.current.length - 1];
      setReplyTargetId(lastMsg ? lastMsg.id : null);
    }
  };

  // Build the full Email object for the composer / translation from a thread row.
  const toEmail = (m: any): Email => ({
    id: m.id, accountId: m.account_id, subject: m.subject,
    from: { name: m.from_name, email: m.from_email },
    to: JSON.parse(m.to_list || '[]'), cc: JSON.parse(m.cc_list || '[]'), bcc: [],
    date: new Date(m.date * 1000), lang: m.detected_lang,
    bodyOriginal: m.body_html || '', bodyTranslated: JSON.parse(m.translations || '{}'),
    attachments: JSON.parse(m.attachments || '[]'), unread: false, starred: Boolean(m.starred),
    folder: m.folder, labels: m.labels ? JSON.parse(m.labels) : [],
  });

  const toggleTranslate = async (m: any) => {
    const id = m.id;
    if (showTrans.has(id)) { setShowTrans(prev => { const n = new Set(prev); n.delete(id); return n; }); return; }
    if (!transBody[id]) {
      const cached = (() => { try { return JSON.parse(m.translations || '{}')[uiLang]; } catch { return ''; } })();
      if (cached && !cached.includes('[No API key configured]')) setTransBody(t => ({ ...t, [id]: cached }));
      else {
        setTransBusy(prev => new Set(prev).add(id));
        try {
          const tr = await translateEmail(id, uiLang);
          setTransBody(t => ({ ...t, [id]: tr || '' }));
        } catch { showToast('Translation failed'); setTransBusy(prev => { const n = new Set(prev); n.delete(id); return n; }); return; }
        setTransBusy(prev => { const n = new Set(prev); n.delete(id); return n; });
      }
    }
    setShowTrans(prev => new Set(prev).add(id));
  };

  const delThread = () => setConfirm({
    msg: `Delete this conversation (${msgs.length} emails)?`,
    action: () => {
      setConfirm(null);
      const ids = msgs.length ? msgs.map(m => m.id) : (root.threadIds || [root.id]);
      void deleteEmails(ids);   // the row leaves at once; a failure brings it back with its own toast
      setActiveEmail(null);
      showToast('Conversation moved to Trash');
    },
  });
  const archThread = () => setConfirm({
    msg: `Archive this conversation (${msgs.length} emails)?`,
    action: () => {
      setConfirm(null);
      const ids = msgs.length ? msgs.map(m => m.id) : (root.threadIds || [root.id]);
      for (const id of ids) archiveEmail(id);
      setActiveEmail(null);
      showToast('Conversation archived');
    },
  });

  const initialsOf = (name: string, em: string) => (name || em || '?').split(' ').map((w: string) => w[0]).join('').slice(0, 2).toUpperCase();
  const last = msgs[msgs.length - 1];
  // Whichever message is expanded drives the Reply/Reply All/Forward buttons in the top bar.
  const replyTarget = msgs.find(m => m.id === replyTargetId) || last;
  const replyTargetName = replyTarget ? (replyTarget.from_name || replyTarget.from_email || '') : '';
  // Reply / Reply All / Forward — the top bar's and each message's two rows. No Reply while the Reply all switch
  // is on: it would only open as Reply All (report 643e2141).
  const replyActs = ([['reply', 'ti-corner-up-left', 'Reply'], ['replyAll', 'ti-corner-up-left-double', 'Reply All'], ['forward', 'ti-corner-up-right', 'Forward']] as const)
    .filter(([mode]) => mode !== 'reply' || !replyAllDefault);
  // Snooze takes the conversation's Inbox messages together — own (personal) mailbox only.
  const snoozeAcct = accounts.find(a => a.id === root.accountId);
  const canSetSnooze = !!snoozeAcct?.personal;
  const snoozeIds = (canSetSnooze || (activeFolder === 'snoozed' && !!snoozeAcct && !snoozeAcct.shared)) ? msgs.filter(m => m.folder === 'inbox').map(m => m.id) : [];

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: 'var(--bg-surface)' }}>
      {/* Header — the reply target with the action bar at the right end of its line, then the subject
          + the message count. */}
      <div style={{ padding: '14px 20px 12px', borderBottom: '1px solid var(--border)', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {/* This line is the width the bar's names are judged against: it is the header's own width,
            whatever the bar holds, so it is what tells the bar whether every name fits. The reply
            target beside the bar is marked data-grow and left out of the measurement — it gives way
            (to an ellipsis, then to nothing) instead of taking room from the buttons. */}
        <div ref={barRef} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10 }}>
          {/* Whose message the bar's Reply / Reply All / Forward answer */}
          {!compose && replyTarget && (
            <span data-grow="1" style={{ flex: 1, minWidth: 0, fontSize: 11.5, color: 'var(--text-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {`→ ${replyTargetName} · ${format(new Date(replyTarget.date * 1000), 'dd/MM · HH:mm')}`}
            </span>
          )}
          {/* ── Action bar — the same row a single email has beside its tabs (Calem, Bugify
              #e5a0a527); a conversation has no tabs, so it ends the header's top line. Left to
              right: Back to the folder (split screen off only) · Unread · Delete · Reply / Reply All /
              Forward · Archive · Snooze, each in its group's tint with its label as its tooltip — and
              wearing its own name whenever the line has room for all of them (Bugify #194ccc7a), or,
              where it has not, saying it in the pill it grows into under the pointer (Bugify
              #338c1651). While the names are up the bar keeps its content's width (it is the width
              the line is measured against) and never wraps; the icon row may give way and stack as
              its last resort. It takes the single email's square size but not its left alignment or
              10px lift, which are measured from the tabs and the line under them (Bugify #3146d7e0). ── */}
          <div style={{ ...barStyle, alignItems: 'center', flexWrap: barCompact ? 'wrap' : 'nowrap', flexShrink: barCompact ? 1 : 0 }}>
            <BarCompact.Provider value={barCompact}>
            {onBack && <BackButton onBack={() => { markAllRead(); onBack?.(); }} />}
            {/* Unread — the newest message goes back to unread and the conversation closes. It clears
                the open email instead of going Back, so the leave-the-thread cleanup doesn't re-read it. */}
            {!compose && (
              <BarButton tint={BAR_TINT.blue} icon="ti-mail" label={t('eml_mark_unread')}
                onClick={() => { markUnread(last?.id || root.id); showToast('Marked as unread'); setActiveEmail(null); }} />
            )}
            <BarButton tint={BAR_TINT.red} icon="ti-trash" label={t('delete')} title={t('eml_bar_delete_conv')} onClick={delThread} />
            {!compose && replyTarget && replyActs.map(([mode, icon]) => (
              <BarButton key={mode} tint={BAR_TINT.green} icon={icon} label={t(mode)}
                onClick={() => setCompose({ email: toEmail(replyTarget), mode: replyModeOf(mode) })} />
            ))}
            <BarButton tint={BAR_TINT.amber} icon="ti-archive" label={t('archive')} title={t('eml_bar_archive_conv')} onClick={archThread} />
            {snoozeIds.length > 0 && <SnoozeButton ids={snoozeIds} canSet={canSetSnooze} />}
            </BarCompact.Provider>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <h2 style={{ flex: 1, fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1.4, minWidth: 0 }}>
            {root.subject} <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-tertiary)' }}>({totalCount || msgs.length || root.threadCount})</span>
          </h2>
          {canLead && (
            <button onClick={() => setAddLead(true)} title={root.lead ? t('ld_change_or_remove') : t('ld_add_to')}
              style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 11px', fontSize: 12, borderRadius: 'var(--radius-md)', border: `1px solid ${root.lead ? '#6D28D9' : 'var(--border)'}`, background: root.lead ? '#F5F3FF' : 'transparent', color: '#6D28D9', cursor: 'pointer', flexShrink: 0, maxWidth: 190 }}>
              <i className="ti ti-target-arrow" style={{ fontSize: 13, flexShrink: 0 }} aria-hidden />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{root.lead ? (root.lead.name || root.lead.company || 'Lead') : t('ld_add_to')}</span>
            </button>
          )}
        </div>
      </div>

      {/* Messages, oldest → newest */}
      <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: `10px 14px ${FAB_ROOM}px`, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {!loading && !loadErr && totalCount > msgs.length && (
          <div style={{ flexShrink: 0, padding: '7px 12px', fontSize: 11.5, color: 'var(--text-tertiary)', background: 'var(--c-gray-50)', border: '1px solid var(--border)', borderRadius: 10, textAlign: 'center' }}>
            Showing the latest {msgs.length} of {totalCount} messages
          </div>
        )}
        {loading && (
          <div style={{ flexShrink: 0, padding: 40, textAlign: 'center', color: 'var(--text-tertiary)' }}>
            <i className="ti ti-loader-2" style={{ fontSize: 22, animation: 'spin 1s linear infinite', display: 'inline-block' }} aria-hidden />
          </div>
        )}
        {loadErr && !loading && (
          <div style={{ flexShrink: 0, padding: 32, textAlign: 'center' }}>
            <i className="ti ti-alert-circle" style={{ fontSize: 30, color: '#E24B4A', display: 'block', marginBottom: 10 }} aria-hidden />
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>Failed to load the conversation.</div>
            <button onClick={() => setReloadTick(v => v + 1)} style={{ padding: '7px 16px', fontSize: 12, borderRadius: 'var(--radius-md)', border: '1px solid var(--accent)', background: 'transparent', color: 'var(--accent)', cursor: 'pointer', fontWeight: 500 }}>Try again</button>
          </div>
        )}
        {!loading && !loadErr && msgs.map(m => {
          const open = expanded.has(m.id);
          const name = m.from_name || m.from_email || '?';
          const dt = new Date(m.date * 1000);
          if (!open) {
            const un = Boolean(m.unread);   // unread rows render inverted (dark bg, light text)
            return (
              <button key={m.id} onClick={() => toggle(m)} style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', padding: '9px 12px', borderRadius: 10, border: un ? '1px solid var(--accent)' : '1px solid var(--border)', background: un ? 'var(--accent)' : 'var(--c-gray-50)', cursor: 'pointer' }}>
                <div style={{ width: 28, height: 28, borderRadius: '50%', background: un ? 'rgba(255,255,255,0.22)' : 'var(--accent-bg)', color: un ? '#fff' : 'var(--accent-text)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, flexShrink: 0 }}>
                  <span style={{ textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic' }}>{initialsOf(m.from_name, m.from_email)}</span>
                </div>
                <span style={{ fontSize: 12.5, fontWeight: un ? 700 : 600, color: un ? '#fff' : 'var(--text-primary)', flexShrink: 0, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
                <span style={{ flex: 1, fontSize: 12, color: un ? 'rgba(255,255,255,0.85)' : 'var(--text-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.preview || ''}</span>
                {un && <span style={{ fontSize: 9.5, fontWeight: 700, color: 'var(--accent)', background: '#fff', borderRadius: 8, padding: '2px 7px', flexShrink: 0, letterSpacing: 0.3 }}>NEW</span>}
                <span style={{ fontSize: 10.5, color: un ? 'rgba(255,255,255,0.85)' : 'var(--text-tertiary)', flexShrink: 0 }}>{format(dt, 'dd/MM · HH:mm')}</span>
              </button>
            );
          }
          const to = (() => { try { return JSON.parse(m.to_list || '[]'); } catch { return []; } })();
          const cc = (() => { try { return JSON.parse(m.cc_list || '[]'); } catch { return []; } })();
          const atts = (() => { try { return JSON.parse(m.attachments || '[]'); } catch { return []; } })();
          const rawBody = m.body_html || (m.body_text
            ? `<pre style="white-space:pre-wrap;font-family:inherit;margin:0">${String(m.body_text).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</pre>`
            : '<p style="color:#888">(no content)</p>');
          const body = showTrans.has(m.id) ? (transBody[m.id] || rawBody) : rawBody;
          return (
            <div key={m.id} ref={el => { cardRefs.current[m.id] = el; }} style={{ flexShrink: 0, border: '1px solid var(--border)', borderRadius: 10, background: 'var(--bg-surface)', overflow: 'hidden' }}>
              <div onClick={() => toggle(m)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', cursor: 'pointer', borderBottom: '1px solid var(--border)' }}>
                <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'var(--accent-bg)', color: 'var(--accent-text)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10.5, fontWeight: 700, flexShrink: 0 }}>
                  <span style={{ textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic' }}>{initialsOf(m.from_name, m.from_email)}</span>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-primary)' }}>{name} <span style={{ fontWeight: 400, color: 'var(--text-tertiary)' }}>&lt;{m.from_email}&gt;</span>
                    <ContactBubble saved={savedContacts[addrKey(m.from_email)]} onAdd={() => setAddContact({ name: m.from_name || '', email: m.from_email })} />
                  </div>
                  {to.length > 0 && <div style={{ fontSize: 11, color: 'var(--text-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>To: {to.join(', ')}{cc.length ? ` · CC: ${cc.join(', ')}` : ''}</div>}
                </div>
                {m.detected_lang && m.detected_lang !== uiLang && (
                  <button onClick={e => { e.stopPropagation(); toggleTranslate(m); }} title={showTrans.has(m.id) ? 'Show original' : 'Translate'}
                    style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 8px', fontSize: 11, borderRadius: 8, border: '1px solid var(--border)', background: showTrans.has(m.id) ? 'var(--bg-active)' : 'transparent', color: 'var(--accent)', cursor: 'pointer', flexShrink: 0 }}>
                    <i className={transBusy.has(m.id) ? 'ti ti-loader-2' : 'ti ti-language'} style={{ fontSize: 12, ...(transBusy.has(m.id) ? { animation: 'spin 1s linear infinite' } : {}) }} aria-hidden />
                    {showTrans.has(m.id) ? 'Original' : 'Translate'}
                  </button>
                )}
                {replyActs.map(([mode, icon, title]) => (
                  <button key={mode} onClick={e => { e.stopPropagation(); setCompose({ email: toEmail(m), mode: replyModeOf(mode) }); }} title={`${title}: ${name}`}
                    style={{ border: 'none', background: 'none', color: 'var(--text-tertiary)', cursor: 'pointer', flexShrink: 0, padding: 2, display: 'flex' }}>
                    <i className={`ti ${icon}`} style={{ fontSize: 14 }} aria-hidden />
                  </button>
                ))}
                <button onClick={e => { e.stopPropagation(); const on = !m.starred; setStar(m.id, on); setMsgs(ms => ms.map(x => x.id === m.id ? { ...x, starred: on ? 1 : 0 } : x)); }}
                  title="Star" style={{ border: 'none', background: 'none', color: m.starred ? '#F5A623' : 'var(--text-tertiary)', cursor: 'pointer', flexShrink: 0 }}>
                  <i className={`ti ti-star${m.starred ? '-filled' : ''}`} style={{ fontSize: 14 }} aria-hidden />
                </button>
                <span style={{ fontSize: 11, color: 'var(--text-tertiary)', flexShrink: 0 }}>{format(dt, 'dd/MM/yyyy · HH:mm')}</span>
              </div>
              <div style={{ padding: '12px 12px 0' }}>
                <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', overflow: 'hidden', background: '#fff' }}>
                  {/* Wands on the message's own pictures only: a translation is words alone. */}
                  <HtmlFrame html={body} onRectifyImage={showTrans.has(m.id) ? undefined : n => setBodyPic({ emailId: m.id, n })} />
                </div>
              </div>
              <AttachmentsPanel emailId={m.id} attachments={atts as any[]} />
              <div style={{ display: 'flex', gap: 6, padding: '10px 12px' }}>
                {replyActs.map(([mode, icon, label]) => (
                  <button key={mode} onClick={() => setCompose({ email: toEmail(m), mode: replyModeOf(mode) })} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 11px', fontSize: 11.5, fontWeight: 500, borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-primary)', cursor: 'pointer' }}>
                    <i className={`ti ${icon}`} style={{ fontSize: 12 }} aria-hidden />{label}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {compose && <Composer email={compose.email} mode={compose.mode} onClose={() => setCompose(null)} />}
      {addLead && last && <AddToLeadModal
        email={{ id: last.id, subject: root.subject, fromName: last.from?.name || '', fromEmail: last.from?.email || '', date: last.date, bodyText: String(last.bodyOriginal || '').replace(/<[^>]+>/g, ' ').slice(0, 4000) }}
        current={root.lead ? { rowId: root.lead.rowId, id: root.lead.id, name: root.lead.name, company: root.lead.company } : undefined}
        onClose={() => setAddLead(false)} onDone={() => { showToast(t('ld_added')); void loadEmails({ silent: true } as any); }} />}
      {addContact && <ContactForm contact={null} initial={addContact} onClose={() => setAddContact(null)}
        onSaved={() => { if (!savedContacts[addrKey(addContact.email)]) showToast(t('eml_contact_added')); markSaved(addContact.email); }} />}
      {confirm && <ConfirmDialog message={confirm.msg} onConfirm={confirm.action} onCancel={() => setConfirm(null)} />}
      {bodyPic && <RectifyModal key={`${bodyPic.emailId}:${bodyPic.n}`} doors={doorsOf(bodyPic.emailId, { image: bodyPic.n })} onClose={() => setBodyPic(null)} />}
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
